/* eslint-disable camelcase */
import axios from 'axios';
import type { Site } from '@hotjar/site';
import type { ApiBaseResponse } from '@hotjar/webapp/domains/common/api';
import { isAllowed } from '@hotjar/feature-flags';
import type { Feedback } from './Feedback';
import type { FeedbackResponsesFilterDto } from './filters/FiltersMappings';
import type { FeedbackResultsDto } from './FeedbackResultsDto';
import { type FeedbackResponseDto } from './FeedbackResponseDto';

function getEndpointPath(
  version: 'v1' | 'v2' | 'v3',
  site: Site,
  widgetId?: number,
  action?: 'move',
): string {
  const separateAskServicePrefix = isAllowed('ask.separate_service', site) ? 'ask/' : '';
  let path = `/api/${separateAskServicePrefix}${version}/sites/${site.id}/feedback`;
  if (widgetId) {
    path = `${path}/${widgetId}`;
  }
  if (action) {
    path = `${path}/${action}`;
  }
  return path;
}

export async function saveFeedbackWidget(site: Site, widget: Feedback): Promise<void> {
  const path = getEndpointPath('v1', site, widget.id);

  const response = await axios.put<ApiBaseResponse>(path, widget);

  if (!response.data.success) {
    throw new Error();
  }
}

export async function toggleWidgetStatus(site: Site, widgetId: number): Promise<void> {
  const path = getEndpointPath('v1', site, widgetId);

  const response = await axios.post<ApiBaseResponse>(path, { action: 'toggle_enabled' });

  if (!response.data.success) {
    throw new Error();
  }
}

export async function getFeedbackWidget(site: Site, widgetId: number): Promise<Feedback> {
  const response = await axios.get(getEndpointPath('v1', site, widgetId));

  return response.data;
}

export async function getFeedbackWidgets(site: Site): Promise<Feedback[]> {
  if (site === undefined) {
    return Promise.reject(new Error('Invalid site'));
  }

  const response = await axios.get(getEndpointPath('v1', site));

  return response.data;
}

export async function deleteFeedbackWidget(site: Site, widgetId: number): Promise<void> {
  if (site === undefined) {
    throw new Error('Invalid site');
  }

  if (widgetId === undefined) {
    throw new Error('Invalid widget id');
  }

  const response = await axios.delete(getEndpointPath('v1', site, widgetId));

  if (!response.data.success) {
    throw new Error();
  }
}

export async function deleteFeedbackResponse(
  site: Site,
  widgetId: number,
  responseId: number,
): Promise<void> {
  if (site === undefined) {
    throw new Error('Invalid site');
  }

  if (widgetId === undefined) {
    throw new Error('Invalid widget id');
  }

  if (responseId === undefined) {
    throw new Error('Invalid widget id');
  }

  const response = await axios.delete(
    `${getEndpointPath('v1', site, widgetId)}/responses/${responseId}`,
  );

  if (!response.data.success) {
    throw new Error();
  }
}

// Adding empty {} result handling in anticipation of changes on RAN-230
export type FeedbackWidgetIdByUrlResult = { id: number } | Record<string, never>;

export async function getFeedbackWidgetIdByUrl(
  site: Site,
  clauses: FeedbackResponsesFilterDto[],
): Promise<FeedbackWidgetIdByUrlResult> {
  const basePath = getEndpointPath('v1', site);

  const response = await axios.get<FeedbackWidgetIdByUrlResult>(`${basePath}/search-by-url`, {
    params: {
      search_spec: JSON.stringify({ clauses }),
    },
  });

  return response.data;
}

export async function getFeedbackWidgetResults(
  site: Site,
  widgetId: number,
  clauses: FeedbackResponsesFilterDto[],
): Promise<FeedbackResultsDto> {
  const basePath = getEndpointPath('v3', site, widgetId);

  const response = await axios.get(`${basePath}/results`, {
    params: {
      survey_query: JSON.stringify({ clauses }),
    },
  });

  return response.data;
}

type FeedbackResponsesData = { data: FeedbackResponseDto[]; success: boolean; count: number };

export async function getFeedbackResponse(
  site: Site,
  widgetId: number,
  responseId: number,
): Promise<FeedbackResponseDto> {
  const basePath = getEndpointPath('v3', site, widgetId);

  const { data } = await axios.get<FeedbackResponsesData>(`${basePath}/responses`, {
    params: {
      search_spec: JSON.stringify({ clauses: [{ type: 'document_id', value: responseId }] }),
    },
  });

  return data.data[0];
}
